import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { mercadopagoPixService } from '../../services/mercadopagoPixService';
import { supabase } from '../../config/supabase';
import { trackEvent } from '../../lib/meta-pixel';
import { Loader2, CreditCard, Zap, AlertCircle, CheckCircle, Copy, Clock, QrCode } from 'lucide-react';
import DirectCheckout from './DirectCheckout';

interface PixCheckoutProps {
  selectedPlan: string;
  planDetails: {
    id: string;
    name: string;
    price: number;
    description: string;
    features: string[];
  };
  onSuccess?: () => void;
  onError?: (error: string) => void;
  pendingPurchase?: any;
  userData?: {
    id?: string;
    name?: string;
    email?: string;
    cpf?: string;
    phone?: string;
  };
  customMetadata?: any;
}

const TOKEN_PACKAGES = [
  { id: 'basic', tokens: 100, price: 10, popular: false },
  { id: 'standard', tokens: 250, price: 25, popular: true },
  { id: 'premium', tokens: 500, price: 50, popular: false },
  { id: 'enterprise', tokens: 1000, price: 100, popular: false }
];

const PixCheckoutComponent: React.FC<PixCheckoutProps> = ({ 
  selectedPlan, 
  planDetails, 
  onSuccess, 
  onError,
  pendingPurchase,
  userData,
  customMetadata
}) => {
  const { user } = useAuth();
  // Usar o plano selecionado das props ao invés do estado local
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'pending' | 'checking' | 'approved' | 'rejected'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [checkingInterval, setCheckingInterval] = useState<NodeJS.Timeout | null>(null);
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [showPendingPayments, setShowPendingPayments] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'checkout_pro'>('pix');
  const [asaasPaymentId, setAsaasPaymentId] = useState<string | null>(null);
  const [purchaseIntentId, setPurchaseIntentId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // Estados para DirectCheckout (Modal)
  const [showDirectCheckout, setShowDirectCheckout] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string>('');
  const [directCheckoutPurchaseId, setDirectCheckoutPurchaseId] = useState<string>('');
  
  // Estado para CPF
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState<string | null>(null);

  // Carregar pagamentos pendentes e CPF ao montar o componente
  useEffect(() => {
    if (user?.id) {
      loadPendingPayments();
      loadUserCpf();
    }
    
    // Track initiation
    trackEvent('InitiateCheckout', {
      content_name: planDetails.name,
      content_ids: [planDetails.id],
      value: planDetails.price,
      currency: 'BRL'
    });
  }, [user?.id]);

  // Limpar interval ao desmontar componente
  useEffect(() => {
    return () => {
      if (checkingInterval) {
        clearInterval(checkingInterval);
      }
    };
  }, [checkingInterval]);

  const loadUserCpf = async () => {
    if (userData?.cpf) {
      setCpf(formatCPF(userData.cpf));
      return;
    }
    
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('users')
        .select('cpf')
        .eq('uid', user.id)
        .single();
      
      if (data?.cpf) {
        setCpf(formatCPF(data.cpf));
      }
    } catch (err) {
      console.error('Erro ao carregar CPF:', err);
    }
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const isValidCPF = (cpf: string) => {
    if (typeof cpf !== "string") return false;
    cpf = cpf.replace(/[\s.-]*/igm, '');
    if (
        !cpf ||
        cpf.length != 11 ||
        cpf == "00000000000" ||
        cpf == "11111111111" ||
        cpf == "22222222222" ||
        cpf == "33333333333" ||
        cpf == "44444444444" ||
        cpf == "55555555555" ||
        cpf == "66666666666" ||
        cpf == "77777777777" ||
        cpf == "88888888888" ||
        cpf == "99999999999" 
    ) {
        return false;
    }
    var soma = 0;
    var resto;
    for (var i = 1; i <= 9; i++) 
        soma = soma + parseInt(cpf.substring(i-1, i)) * (11 - i);
    resto = (soma * 10) % 11;
    if ((resto == 10) || (resto == 11))  resto = 0;
    if (resto != parseInt(cpf.substring(9, 10)) ) return false;
    soma = 0;
    for (var i = 1; i <= 10; i++) 
        soma = soma + parseInt(cpf.substring(i-1, i)) * (12 - i);
    resto = (soma * 10) % 11;
    if ((resto == 10) || (resto == 11))  resto = 0;
    if (resto != parseInt(cpf.substring(10, 11) ) ) return false;
    return true;
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    
    if (cpfError) {
        const numbers = formatted.replace(/\D/g, '');
        if (numbers.length === 11 && isValidCPF(numbers)) {
            setCpfError(null);
        }
    }
  };

  const loadPendingPayments = async () => {
    if (!user?.id) return;
    
    try {
      const pending = await mercadopagoPixService.getPendingPayments(user.id);
      setPendingPayments(pending);
    } catch (error) {
      console.error('Erro ao carregar pagamentos pendentes:', error);
    }
  };

  const handleDirectCheckoutSuccess = () => {
    setPaymentStatus('approved');
    setShowDirectCheckout(false);
    onSuccess?.();
  };

  const handlePixPayment = async () => {
    if (!user && !userData) {
      setError('Usuário não identificado');
      return;
    }

    // Validar CPF
    const cpfClean = cpf.replace(/\D/g, '');
    if (!cpfClean || cpfClean.length !== 11) {
        setCpfError('CPF é obrigatório para gerar o PIX');
        return;
    }
    
    if (!isValidCPF(cpfClean)) {
        setCpfError('CPF inválido. Verifique os números digitados.');
        return;
    }

    setIsProcessing(true);
    setError(null);
    setCpfError(null);
    setPaymentStatus('pending');

    try {
      // Determinar quantidade de tokens baseado no plano/descrição
      let tokenAmount = 0;
      // Tentar extrair do description se for customizado
      const tokenMatch = planDetails.description.match(/(\d+)\s+Tokens/i);
      if (tokenMatch) {
        tokenAmount = parseInt(tokenMatch[1]);
      } else {
        // Fallback para planos fixos se necessário
        if (planDetails.id === 'premium') tokenAmount = 500;
        if (planDetails.id === 'enterprise') tokenAmount = 1000;
      }

      console.log('Iniciando pagamento PIX via Asaas...');
      console.log('Tokens:', tokenAmount);
      console.log('Valor:', planDetails.price);

      // Usar Edge Function do Asaas
      const { data: result, error: funcError } = await supabase.functions.invoke('asaas-create-pix-payment', {
        body: {
          amount: planDetails.price,
          tokens: tokenAmount || 100, // Fallback seguro
          description: planDetails.description,
          cpfCnpj: cpfClean,
          metadata: customMetadata || {},
          user_id: userData?.id || user?.id
        }
      });

      // Se der sucesso e o CPF for válido, salvar no perfil do usuário para o futuro
      if (!funcError && result && !result.error) {
        try {
            await supabase.from('users').update({ cpf: cpfClean }).eq('uid', user.id);
        } catch (saveError) {
            console.warn('Não foi possível salvar o CPF no perfil do usuário:', saveError);
            // Não falhar o pagamento por isso
        }
      }

      if (funcError || !result || result.error) {
        throw new Error(result?.error || funcError?.message || 'Erro ao criar pagamento Asaas');
      }

      console.log('Pagamento Asaas criado:', result);

      setAsaasPaymentId(result.paymentId);
      setPurchaseIntentId(result.intentId);
      setQrCode(result.qrCode);
      setPixCode(result.copyPaste);
      
      // Iniciar verificação de status (Polling no Purchase Intent)
      startAsaasStatusCheck(result.intentId);
      
    } catch (err: any) {
      console.error('Erro no pagamento PIX:', err);
      setError(err.message || 'Erro ao processar pagamento PIX');
      setPaymentStatus('rejected');
      onError?.(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const startAsaasStatusCheck = (intentId: string) => {
    setPaymentStatus('checking');
    
    const interval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('purchase_intents')
          .select('status')
          .eq('id', intentId)
          .single();

        if (data && data.status === 'completed') {
          console.log('✅ Pagamento Asaas confirmado!');
          setPaymentStatus('approved');
          clearInterval(interval);
          setCheckingInterval(null);
          
          trackEvent('Purchase', {
            content_name: planDetails.name,
            content_ids: [planDetails.id],
            value: planDetails.price,
            currency: 'BRL'
          });
          
          onSuccess?.();
        } else if (data && data.status === 'failed') {
          setPaymentStatus('rejected');
          setError('Pagamento falhou ou foi cancelado.');
          clearInterval(interval);
          setCheckingInterval(null);
        }
      } catch (error) {
        console.error('Erro ao verificar status do pagamento Asaas:', error);
      }
    }, 3000); // Verificar a cada 3 segundos

    setCheckingInterval(interval);

    // Parar verificação após 15 minutos
    setTimeout(() => {
      if (paymentStatus === 'checking') {
        clearInterval(interval);
        setCheckingInterval(null);
        setPaymentStatus('idle');
        setError('Tempo limite para verificação. Se você pagou, aguarde a confirmação por email.');
      }
    }, 15 * 60 * 1000);
  };

  const handleCheckoutPro = async () => {
    const userId = userData?.id || user?.id;
    const userEmail = userData?.email || user?.email;
    const userName = userData?.name || user?.user_metadata?.full_name || user?.email || 'Usuário';

    if (!userId || !userEmail) {
      setError('Usuário não identificado ou sem email.');
      return;
    }

    if (!planDetails.price || isNaN(planDetails.price) || planDetails.price <= 0) {
      setError('Preço do plano inválido. Tente recarregar a página.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setPaymentStatus('pending');

    try {
      // Determinar quantidade de tokens
      let tokenAmount = 0;
      const tokenMatch = planDetails.description.match(/(\d+)\s+Tokens/i);
      if (tokenMatch) {
        tokenAmount = parseInt(tokenMatch[1]);
      } else {
        if (planDetails.id === 'premium') tokenAmount = 500;
        if (planDetails.id === 'enterprise') tokenAmount = 1000;
      }

      // Criar novo pagamento
      const paymentData = {
        userId,
        userEmail,
        userName,
        amount: planDetails.price,
        description: `Assinatura ${planDetails.name} - Overpixel`,
        tokens: tokenAmount // Passar tokens explicitamente
      };

      console.log('Criando Checkout Pro:', paymentData);

      const result = await mercadopagoPixService.createCheckoutProPreference(paymentData);
      
      console.log('Resultado do Checkout Pro:', result);

      setPreferenceId(result.preferenceId);
      
      // Abrir Modal do Checkout Pro ao invés de redirecionar
      if (result.initPoint) {
        setCheckoutUrl(result.initPoint);
        setDirectCheckoutPurchaseId(result.preferenceId); // Usando preferenceId como purchaseId para rastreio
        setShowDirectCheckout(true);
      }
      
    } catch (err: any) {
      console.error('Erro no Checkout Pro:', err);
      
      // Mensagens de erro mais específicas
      let errorMessage = 'Erro ao processar Checkout Pro';
      
      if (err.message?.includes('Invalid credentials') || err.message?.includes('401')) {
        errorMessage = 'Erro de configuração do sistema de pagamentos. Entre em contato com o suporte.';
      } else if (err.message?.includes('Invalid email')) {
        errorMessage = 'Email inválido. Verifique seus dados de cadastro.';
      } else if (err.message?.includes('Network')) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setPaymentStatus('rejected');
      onError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const checkPaymentStatus = async (paymentId: string) => {
    try {
      setPaymentStatus('checking');
      const status = await mercadopagoPixService.checkPaymentStatus(paymentId);
      
      console.log('Status do pagamento:', status);
      
      if (status.approved) {
        setPaymentStatus('approved');
        trackEvent('Purchase', {
          content_name: planDetails.name,
          content_ids: [planDetails.id],
          value: planDetails.price,
          currency: 'BRL'
        });
        onSuccess?.();
      } else if (status.error) {
        console.error('Erro ao verificar status:', status.error);
        setPaymentStatus('rejected');
        setError(status.error);
      }
    } catch (error) {
      console.error('Erro ao verificar status do pagamento:', error);
      setPaymentStatus('rejected');
      setError('Erro ao verificar pagamento');
    }
  };

  const startPaymentStatusCheck = (prefId: string) => {
    setPaymentStatus('checking');
    
    const interval = setInterval(async () => {
      try {
        const status = await mercadopagoPixService.checkPaymentStatus(prefId);
        
        console.log('Status do pagamento:', status);
        
        if (status.approved) {
          setPaymentStatus('approved');
          clearInterval(interval);
          setCheckingInterval(null);
          trackEvent('Purchase', {
            content_name: planDetails.name,
            content_ids: [planDetails.id],
            value: planDetails.price,
            currency: 'BRL'
          });
          onSuccess?.();
        } else if (status.error) {
          console.error('Erro ao verificar status:', status.error);
          // Continuar verificando, pode ser erro temporário
        }
      } catch (error) {
        console.error('Erro ao verificar status do pagamento:', error);
        // Continuar verificando
      }
    }, 3000); // Verificar a cada 3 segundos

    setCheckingInterval(interval);

    // Parar verificação após 10 minutos
    setTimeout(() => {
      clearInterval(interval);
      setCheckingInterval(null);
      if (paymentStatus === 'checking') {
        setPaymentStatus('idle');
        setError('Tempo limite para verificação do pagamento. Verifique manualmente.');
      }
    }, 10 * 60 * 1000);
  };

  const copyPixCode = () => {
    if (pixCode) {
      navigator.clipboard.writeText(pixCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetPayment = () => {
    setPaymentStatus('idle');
    setError(null);
    setPreferenceId(null);
    setQrCode(null);
    setPixCode(null);
    if (checkingInterval) {
      clearInterval(checkingInterval);
      setCheckingInterval(null);
    }
  };

  const resumePendingPayment = async (payment: any) => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // Buscar detalhes do pagamento no Mercado Pago
      const paymentDetails = await mercadopagoPixService.getPaymentStatus(payment.payment_id);
      
      if (paymentDetails.status === 'approved') {
        setPaymentStatus('approved');
        if (onSuccess) onSuccess();
        return;
      }
      
      if (paymentDetails.status === 'rejected' || paymentDetails.status === 'cancelled') {
        setPaymentStatus('rejected');
        return;
      }
      
      // Se ainda está pendente, mostrar QR code novamente
      if (paymentDetails.qr_code_base64) {
        setQrCode(paymentDetails.qr_code_base64);
        setPixCode(paymentDetails.qr_code);
        setPreferenceId(payment.payment_id);
        setPaymentStatus('checking');
        checkPaymentStatus(payment.payment_id);
      }
    } catch (error) {
      console.error('Erro ao retomar pagamento:', error);
      setError('Erro ao retomar pagamento. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!user && !userData) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
        <p className="text-gray-600">Faça login para continuar com o pagamento</p>
      </div>
    );
  }

  // Determinar quantidade de tokens para passar ao DirectCheckout
  let tokenAmountForModal = 0;
  const tokenMatch = planDetails.description.match(/(\d+)\s+Tokens/i);
  if (tokenMatch) {
    tokenAmountForModal = parseInt(tokenMatch[1]);
  } else {
    if (planDetails.id === 'premium') tokenAmountForModal = 500;
    if (planDetails.id === 'enterprise') tokenAmountForModal = 1000;
  }

  return (
    <>
      {/* Modal de Checkout Pro */}
      <DirectCheckout
        isOpen={showDirectCheckout}
        onClose={() => setShowDirectCheckout(false)}
        checkoutUrl={checkoutUrl}
        purchaseId={directCheckoutPurchaseId}
        tokenAmount={tokenAmountForModal || 100}
        onPaymentSuccess={handleDirectCheckoutSuccess}
        preferenceId={preferenceId || undefined}
      />
      
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
        Recarga de Tokens
      </h2>

      {/* Pagamentos Pendentes */}
      {pendingPayments.length > 0 && paymentStatus === 'idle' && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-700">Pagamentos Pendentes</h3>
            <button
              onClick={() => setShowPendingPayments(!showPendingPayments)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showPendingPayments ? 'Ocultar' : `Ver ${pendingPayments.length} pendente(s)`}
            </button>
          </div>
          
          {showPendingPayments && (
            <div className="space-y-3">
              {pendingPayments.map((payment, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      <div>
                        <p className="font-medium text-gray-800">
                          R$ {payment.reais_amount?.toFixed(2) || '0.00'} - {payment.amount} tokens
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(payment.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => resumePendingPayment(payment)}
                      disabled={isProcessing}
                      className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {isProcessing ? 'Carregando...' : 'Retomar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plano Selecionado */}
      {paymentStatus === 'idle' && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Plano Selecionado:</h3>
          <div className="p-6 border-2 border-blue-500 bg-blue-50 rounded-lg">
            <div className="text-center">
              <CreditCard className="mx-auto h-8 w-8 text-blue-500 mb-2" />
              <h4 className="font-semibold text-gray-800 text-xl">{planDetails.name}</h4>
              <p className="text-3xl font-bold text-blue-600 mb-2">R$ {planDetails.price.toFixed(2)}</p>
              <p className="text-gray-600 mb-4">{planDetails.description}</p>
              <div className="text-left">
                <h5 className="font-semibold text-gray-700 mb-2">Recursos inclusos:</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  {planDetails.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                  {planDetails.features.length > 3 && (
                    <li className="text-blue-600">+ {planDetails.features.length - 3} outros recursos</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seleção de Método de Pagamento */}
      {paymentStatus === 'idle' && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-4">Escolha o método de pagamento:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setPaymentMethod('pix')}
              className={`p-4 border-2 rounded-lg transition-all ${
                paymentMethod === 'pix'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  paymentMethod === 'pix'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {paymentMethod === 'pix' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                  )}
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-800">PIX</div>
                  <div className="text-sm text-gray-600">Aprovação instantânea</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setPaymentMethod('checkout_pro')}
              className={`p-4 border-2 rounded-lg transition-all ${
                paymentMethod === 'checkout_pro'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  paymentMethod === 'checkout_pro'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {paymentMethod === 'checkout_pro' && (
                    <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                  )}
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-800">Checkout Pro</div>
                  <div className="text-sm text-gray-600">Cartão, PIX e mais</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Resumo do Pedido */}
      {paymentStatus === 'idle' && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="font-semibold text-gray-800 mb-2">Resumo do Pedido:</h4>
          <div className="flex justify-between items-center">
            <span>Assinatura {planDetails.name}</span>
            <span className="font-bold text-green-600">R$ {planDetails.price.toFixed(2)}</span>
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {paymentMethod === 'pix' ? 'Pagamento via PIX • Aprovação instantânea' : 'Checkout Pro • Múltiplas formas de pagamento'}
          </div>
        </div>
      )}

      {/* Input de CPF para PIX */}
      {paymentStatus === 'idle' && paymentMethod === 'pix' && (
        <div className="mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
          <label htmlFor="cpf-input" className="block text-sm font-medium text-gray-700 mb-1">
            CPF (Obrigatório para PIX)
          </label>
          <input
            id="cpf-input"
            type="text"
            value={cpf}
            onChange={handleCpfChange}
            placeholder="000.000.000-00"
            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 transition-all text-gray-900 ${
              cpfError 
                ? 'border-red-300 focus:ring-red-200 bg-red-50' 
                : 'border-gray-300 focus:ring-blue-500 focus:border-transparent bg-white'
            }`}
            maxLength={14}
          />
          {cpfError && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {cpfError}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Seu CPF é necessário para emissão da nota fiscal e validação do pagamento.
          </p>
        </div>
      )}

      {/* Botão de Pagamento */}
      {paymentStatus === 'idle' && (
        <button
          onClick={paymentMethod === 'pix' ? handlePixPayment : handleCheckoutPro}
          disabled={isProcessing}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Processando...
            </>
          ) : (
            <>
              <CreditCard className="h-5 w-5" />
              {paymentMethod === 'pix' ? 'Pagar com PIX' : 'Ir para Checkout Pro'}
            </>
          )}
        </button>
      )}

      {/* Status do Pagamento */}
      {paymentStatus === 'pending' && (
        <div className="text-center p-6">
          <Loader2 className="mx-auto h-12 w-12 text-blue-500 animate-spin mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Preparando pagamento PIX...</h3>
          <p className="text-gray-600">Aguarde enquanto geramos seu código PIX</p>
        </div>
      )}

      {paymentStatus === 'checking' && (
        <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200 shadow-sm animate-in fade-in duration-300">
          <div className="flex items-center justify-center gap-3 mb-6">
             <div className="animate-pulse bg-green-100 p-3 rounded-full shadow-sm">
                <CreditCard className="h-6 w-6 text-green-600" />
             </div>
             <h3 className="text-xl font-bold text-gray-800">Pagamento Pendente</h3>
          </div>
          
          <div className="mb-8">
            <p className="text-sm font-medium text-gray-500 mb-1 uppercase tracking-wide">Valor a pagar</p>
            <p className="text-4xl font-extrabold text-gray-900">R$ {planDetails.price.toFixed(2)}</p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-8 inline-block relative group hover:shadow-md transition-shadow">
            {qrCode ? (
              <>
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-t-xl"></div>
                <img 
                  src={`data:image/png;base64,${qrCode}`} 
                  alt="QR Code PIX" 
                  className="mx-auto w-56 h-56 object-contain"
                />
                <p className="text-xs text-gray-400 mt-3 font-medium">Escaneie com o app do seu banco</p>
              </>
            ) : (
              <div className="w-56 h-56 flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
                  <span className="text-xs text-gray-400">Gerando QR Code...</span>
                </div>
              </div>
            )}
          </div>

          <div className="max-w-md mx-auto mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2 text-left ml-1">
              PIX Copia e Cola
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input 
                  type="text" 
                  readOnly 
                  value={pixCode || 'Gerando código...'} 
                  className="w-full p-3 pl-4 pr-10 border border-gray-300 rounded-lg bg-white text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono shadow-sm"
                  onClick={(e) => e.currentTarget.select()}
                />
              </div>
              <button
                onClick={copyPixCode}
                disabled={!pixCode}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 shadow-sm min-w-[140px] justify-center ${
                  copied 
                    ? 'bg-green-600 text-white hover:bg-green-700 ring-2 ring-green-600 ring-offset-2' 
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {copied ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copiar
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-left ml-1">
              Cole este código na opção "PIX Copia e Cola" do seu banco.
            </p>
          </div>
          
          <div className="bg-blue-50/80 p-4 rounded-xl border border-blue-100 text-sm text-blue-900 mb-6 text-left shadow-sm">
            <div className="flex items-start gap-3">
               <div className="bg-white p-1.5 rounded-full shadow-sm mt-0.5">
                 <Zap className="h-4 w-4 text-blue-600" />
               </div>
               <div>
                 <p className="font-bold text-blue-900 mb-1">Liberação Imediata</p>
                 <p className="text-blue-700/80 text-xs leading-relaxed">
                   Seu pagamento é identificado automaticamente pelo nosso sistema em poucos segundos. Não precisa enviar comprovante.
                 </p>
               </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-6 border-t border-gray-200 pt-6">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <span className="text-xs font-medium text-gray-600">Aguardando pagamento...</span>
            </div>
            <div className="h-4 w-px bg-gray-300"></div>
            <div className="flex items-center gap-1.5">
              <div className="bg-gray-200 p-1 rounded-full">
                <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
              </div>
              <span className="text-xs font-medium text-gray-600">Ambiente Seguro</span>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'approved' && (
        <div className="text-center p-6">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h3 className="text-lg font-semibold text-green-800 mb-2">Pagamento Aprovado!</h3>
          <p className="text-gray-600 mb-4">
            Sua assinatura {planDetails.name} foi ativada com sucesso!
          </p>
          <button
            onClick={resetPayment}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Voltar
          </button>
        </div>
      )}

      {paymentStatus === 'rejected' && (
        <div className="text-center p-6">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Pagamento Rejeitado</h3>
          <p className="text-gray-600 mb-4">
            Houve um problema com seu pagamento. Tente novamente.
          </p>
          <button
            onClick={resetPayment}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <span className="font-semibold">Erro:</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Informações Adicionais */}
      <div className="mt-8 text-xs text-gray-400 text-center border-t border-gray-100 pt-6">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
            Pagamento 100% Seguro
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
            Liberação Automática
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
            Suporte 24/7
          </div>
        </div>
        <p className="mt-3 opacity-75">Overpixel © 2025 - Todos os direitos reservados</p>
      </div>
    </div>
    </>
  );
};

export default PixCheckoutComponent;